import React from "react";
import { Button, Table, Tag, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";

import FrequencyTable from "./FrequencyTable";
import ShortformCard from "./ShortformCard";
import AvailableTodayCard from "./AvailableTodayCard";
import { CATEGORY_COLOR, FAMILIES, POOLS, SAMPLE_PATIENT } from "./sampleData";

/**
 * Surface 2 — a collected breakdown, read at a glance.
 *
 * Rebuilt against the way Manulife sets its own breakdown, because a carrier
 * PDF is a document rather than a dashboard and reads far better for it:
 *
 *   - TWO tables, not one. "How much is covered" and "how often can it be done"
 *     are different questions asked at different moments; braiding them into one
 *     wide table is what made the earlier draft dense.
 *   - The maximum is stated ONCE and spanned down the families that share it —
 *     Manulife merges a single cell across eleven rows. Repeating "$1,500" on
 *     every row makes one maximum look like eleven.
 *   - Adult and child frequencies get their own columns, because they genuinely
 *     differ and a single value silently picks one patient to be right about.
 *
 * Typography does the work that borders used to: one large figure for the money,
 * tabular numerals so columns align, quiet uppercase section labels, and air.
 */
export default function DisplayView({ state, onEdit }) {
  const plan = state.plan || {};
  const money = state.money || {};
  const elig = state.eligibility || {};
  const hyg = state.hygiene || {};
  const famState = state.families || {};

  /** Families in pool order, so the spanned maximum cell is contiguous. */
  const poolOrder = ["annual", "ortho"];
  const coverageRows = [];
  poolOrder.forEach(function (poolKey) {
    FAMILIES.filter(function (f) { return (f.pool || "annual") === poolKey; }).forEach(function (f) {
      const v = famState[f.key] || {};
      coverageRows.push({
        key: f.key,
        name: f.name,
        codeList: (f.codes || []).map(function (c) { return c.code; }).join(", "),
        codeNames: (f.codes || []).map(function (c) { return c.code + " — " + c.name; }).join("\n"),
        category: v.category || f.defaultCategory,
        defaultCategory: f.defaultCategory,
        pct: v.pct,
        pool: poolKey,
        exception: v.exception,
        adult: v.adult,
        child: v.child,
      });
    });
  });

  /** Row index where each pool starts, and how many rows it spans. */
  const poolSpan = {};
  coverageRows.forEach(function (r, i) {
    if (!poolSpan[r.pool]) poolSpan[r.pool] = { start: i, count: 0 };
    poolSpan[r.pool].count += 1;
  });

  const coverageColumns = [
    {
      title: "Package",
      dataIndex: "name",
      render: function (_, row) {
        return (
          <div>
            <span className="bw-doc-family">{row.name}</span>
            <Tooltip title={row.codeNames}>
              <div className="bw-doc-codes">{row.codeList}</div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 150,
      render: function (v, row) {
        return (
          <span>
            <Tag color={CATEGORY_COLOR[v]}>{v}</Tag>
            {v !== row.defaultCategory ? <span className="bw-changed">changed</span> : null}
          </span>
        );
      },
    },
    {
      title: "Coverage",
      dataIndex: "pct",
      width: 120,
      align: "right",
      render: function (v) {
        return v ? <span className="bw-doc-pct">{v}%</span> : <span className="bw-notdue">—</span>;
      },
    },
    {
      title: "Maximum",
      dataIndex: "pool",
      width: 240,
      align: "center",
      // The Manulife move: one cell spanning every family that draws on the
      // same pool, so the shared maximum reads as one fact.
      onCell: function (row, index) {
        const span = poolSpan[row.pool] || { start: 0, count: 1 };
        return { rowSpan: index === span.start ? span.count : 0 };
      },
      render: function (poolKey) {
        const pool = POOLS[poolKey] || {};
        return (
          <div className="bw-doc-max">
            <div className="bw-doc-max-amount">${Number(pool.amount).toLocaleString("en-US")}</div>
            <div className="bw-doc-max-period">{pool.period}</div>
            {pool.note ? <div className="bw-doc-max-note">{pool.note}</div> : null}
          </div>
        );
      },
    },
    {
      // Exceptions sit on the family they qualify, in their own column, so a
      // reader scanning one row gets its caveat without hunting a banner.
      title: "Exceptions",
      dataIndex: "exception",
      width: 260,
      render: function (v) {
        return v ? <span className="bw-row-exception">{v}</span> : <span className="bw-notdue">—</span>;
      },
    },
  ];

  const renews = [money.renewsMonth, money.renewsDay].filter(Boolean).join("/");
  const annual = POOLS.annual;
  const ortho = POOLS.ortho;

  return (
    <div className="bw-doc">
      {/* Masthead — identity only. Everything else earns its place below. */}
      <div className="bw-doc-head">
        <div>
          <h2 className="bw-doc-patient">{SAMPLE_PATIENT.name}</h2>
          <div className="bw-doc-plan">
            {plan.carrier} · plan {plan.policyNumber} · certificate {plan.certificateNumber}
          </div>
        </div>
        <Button icon={<EditOutlined />} onClick={onEdit}>Edit worksheet</Button>
      </div>

      {/* The money, stated once and large. This is the number the whole page
          exists to communicate, so it is the only thing set at this size. */}
      <div className="bw-doc-money">
        {/* A line for Basic and a line for Major, because they are two limits
            even when one pool funds them — and whether they are combined is the
            fact that decides how the remaining balance behaves, so it is stated
            outright rather than implied by a shared figure. */}
        <div className="bw-doc-money-main">
          <div className="bw-max-figures">
            <div className="bw-max-line">
              <span className="bw-max-name">Basic</span>
              <span className="bw-max-figure">${Number(money.maxBasic || 0).toLocaleString("en-US")}</span>
            </div>
            <div className="bw-max-line">
              <span className="bw-max-name">Major</span>
              <span className="bw-max-figure">${Number(money.maxMajor || 0).toLocaleString("en-US")}</span>
            </div>
          </div>

          {/* The brace is the argument: it ties both figures to one status, which
              is the same thing Manulife's merged maximum cell does with a single
              spanned box. Drawn only when the pool IS shared — bracketing two
              separate maximums together would assert the opposite of the truth. */}
          {money.maxCombined ? <div className="bw-max-brace" aria-hidden="true"><i /></div> : null}

          <div className={"bw-max-status" + (money.maxCombined ? " is-combined" : "")}>
            {money.maxCombined ? (
              <>
                <b>Combined</b>
                <span>
                  one ${Number(annual.amount).toLocaleString("en-US")} pool
                  <em>{annual.period} · resets {annual.resets}</em>
                </span>
              </>
            ) : (
              <>
                <b>Not combined</b>
                <span>
                  separate maximums
                  <em>{annual.period} · resets {annual.resets}</em>
                </span>
              </>
            )}
          </div>
        </div>
        <div className="bw-doc-money-side">
          <div className="bw-doc-money-row">
            <span>Orthodontics</span>
            <b>${Number(ortho.amount).toLocaleString("en-US")} <em>{ortho.period}</em></b>
          </div>
          <div className="bw-doc-money-row">
            <span>Deductible</span>
            <b>${money.deductibleSingle} <em>single · ${money.deductibleFamily} family</em></b>
          </div>
          <div className="bw-doc-money-row">
            <span>Fee guide</span>
            <b>{money.feeGuide === "C" ? "Current" : "1 year behind"} <em>specialist {money.specialistFee === "Y" ? "eligible" : "not covered"}</em></b>
          </div>
        </div>
      </div>

      {/* Plan rules and what the patient can have today sit directly under the
          maximum: together they answer "can I book this now", which is asked
          far more often than any single coverage percentage. */}
      <div className="bw-doc-brief">
      <section className="bw-doc-section">
        <h3 className="bw-doc-section-title">Plan rules</h3>
        {/* Grouped into the three kinds of fact these actually are — timing,
            who is eligible, and how the office bills — because eight
            identically-set pairs give the eye nothing to hold on to. Labels sit
            in a fixed column so the values form a true vertical axis, and the
            two booleans become state chips rather than rows saying "Yes". */}
        <div className="bw-rules">
          <div className="bw-rules-group">
            <div className="bw-rules-group-label">Timing</div>
            <dl className="bw-rules-list">
              <dt>Recall cycle</dt>
              <dd>
                {hyg.recallCycle} months
                {hyg.recallCycleChildren
                  ? <em> · {hyg.recallCycleChildren} under {hyg.recallSplitAge}</em>
                  : null}
              </dd>
              <dt>Scaling units</dt>
              <dd>{hyg.scalingUnits} <em>per {String(hyg.scalingBasis || "").toLowerCase()}</em></dd>
              <dt>Renews</dt>
              <dd>{renews}</dd>
            </dl>
          </div>

          <div className="bw-rules-group">
            <div className="bw-rules-group-label">Eligibility</div>
            <dl className="bw-rules-list">
              <dt>Dependants</dt>
              <dd>to {elig.dependentAge} <em>years</em></dd>
              <dt>FT students</dt>
              <dd>to {elig.ftStudentAge} <em>years</em></dd>
              <dt>Exams</dt>
              <dd className="bw-rules-prose">{elig.examLimitations}</dd>
            </dl>
          </div>

          <div className="bw-rules-group">
            <div className="bw-rules-group-label">Billing</div>
            <div className="bw-rules-flags">
              <span className={"bw-flag" + (plan.edi ? " is-on" : "")}>EDI</span>
              <span className={"bw-flag" + (plan.assignment === "Y" ? " is-on" : "")}>Assignment</span>
            </div>
          </div>
        </div>
      </section>
        <AvailableTodayCard />
      </div>

      <section className="bw-doc-section">
        <h3 className="bw-doc-section-title">Coverage</h3>
        <Table
          className="bw-doc-table"
          size="middle" rowKey="key" pagination={false} tableLayout="fixed"
          columns={coverageColumns} dataSource={coverageRows}
        />
      </section>

      <section className="bw-doc-section">
        <h3 className="bw-doc-section-title">
          Frequency
          <span className="bw-doc-section-note">limited by age and procedure code</span>
        </h3>
        <FrequencyTable values={state.frequency || {}} editable={false} />
      </section>

      <ShortformCard state={state} compact />
    </div>
  );
}
